import React, { useEffect, useState } from 'react';
import Swal from 'sweetalert2';
import { useAuth } from '../../Constant/Auth';
import '../parentTables.css';
import SearchableSelect from '../../shared/SearchableSelect';

import {
  getReservationsByLibrary,
  cancelReservation,
  getErrorMessage
} from '../../api/api';

const RESERVATION_STATUS_OPTIONS = [
  { value: 'requested', label: 'Requested' },
  { value: 'ready_for_pickup', label: 'Ready For Pickup' },
  { value: 'collected', label: 'Collected' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'rejected', label: 'Rejected' }
];

const MyReservations = () => {
  const { user } = useAuth();

  const libraryId = user?.library_id || user?.libraryId;
  const parentId = user?.member_id || user?.id;

  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');

  const fetchReservations = async () => {
    try {
      if (!libraryId) return;

      setLoading(true);

      const data = await getReservationsByLibrary(libraryId);

      const myData = (data || []).filter((item) => {
        return (
          String(item.member_id || item.user_id || item.parent_id) ===
          String(parentId)
        );
      });

      setReservations(myData);
    } catch (error) {
      console.error('Parent reservations error:', error);
      Swal.fire('Error', getErrorMessage(error), 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReservations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [libraryId, parentId]);

  const handleCancel = async (reservation) => {
    const confirm = await Swal.fire({
      title: 'Cancel Request?',
      text: 'Do you want to cancel this book request?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, cancel',
      cancelButtonText: 'No'
    });

    if (!confirm.isConfirmed) return;

    try {
      await cancelReservation(reservation.id, {
        remarks: 'Cancelled by parent'
      });

      Swal.fire('Success', 'Request cancelled successfully', 'success');
      fetchReservations();
    } catch (error) {
      Swal.fire('Error', getErrorMessage(error), 'error');
    }
  };

  const getStatusBadge = (status) => {
    if (status === 'requested') return 'bg-warning text-dark';
    if (status === 'approved') return 'bg-info';
    if (status === 'ready_for_pickup') return 'bg-primary';
    if (status === 'collected') return 'bg-success';
    if (status === 'cancelled') return 'bg-secondary';
    if (status === 'rejected') return 'bg-danger';
    if (status === 'expired') return 'bg-dark';
    return 'bg-light text-dark';
  };

  const filteredReservations = statusFilter
    ? reservations.filter((item) => item.status === statusFilter)
    : reservations;

  return (
    <div className="container-fluid mt-3 pt-page">
      <div className="pt-page-header">
        <div className="pt-page-header__left">
          <span className="pt-page-header__icon">
            <i className="fas fa-bookmark" />
          </span>
          <div>
            <h3 className="pt-page-header__title">My Book Requests</h3>
            <div className="pt-page-header__subtitle">Track your requested books</div>
          </div>
        </div>
      </div>

      <div className="card shadow-sm mt-3 table-full-card">
        <div className="card-body">
          <div className="row mb-3">
            <div className="col-md-3">
              <SearchableSelect
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                options={RESERVATION_STATUS_OPTIONS}
                placeholder="All Status"
              />
            </div>

            <div className="col-md-2">
              <button
                className="btn btn-outline-secondary"
                onClick={() => setStatusFilter('')}
              >
                Reset
              </button>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-4">Loading...</div>
          ) : (
            <div className="table-responsive">
              <table className="table table-bordered table-hover align-middle">
                <thead className="table-light">
                  <tr>
                    <th>#</th>
                    <th>Book</th>
                    <th>Author</th>
                    <th>Requested On</th>
                    <th>Assigned Copy</th>
                    <th>Status</th>
                    <th>Remarks</th>
                    <th>Action</th>
                  </tr>
                </thead>

                <tbody>
                  {filteredReservations.length > 0 ? (
                    filteredReservations.map((item, index) => (
                      <tr key={item.id}>
                        <td>{index + 1}</td>
                        <td><strong>{item.title || item.book_title || '-'}</strong></td>
                        <td>{item.author_name || '-'}</td>
                        <td>
                          {item.requested_at
                            ? String(item.requested_at).slice(0, 10)
                            : item.created_at
                              ? String(item.created_at).slice(0, 10)
                              : '-'}
                        </td>
                        <td>
                          {item.accession_no || item.copy_accession_no || '-'}
                          <div className="small text-muted">
                            {item.barcode || item.copy_barcode || ''}
                          </div>
                        </td>
                        <td>
                          <span className={`badge ${getStatusBadge(item.status)}`}>
                            {item.status || '-'}
                          </span>
                        </td>
                        <td>{item.remarks || '-'}</td>
                        <td>
                          {item.status === 'requested' ? (
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => handleCancel(item)}
                            >
                              Cancel
                            </button>
                          ) : (
                            <span className="text-muted small">No action</span>
                          )}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="8" className="text-center text-muted py-4">
                        No requests found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MyReservations;